/**
 * Page « À propos ».
 *
 * Sept sections atteignables par un sommaire lateral. Le contenu est le meme
 * qu'avant la refonte — c'est la mise en page qui change : titre d'affiche,
 * corps de texte a 16 px, tableau de prerequis en monospace.
 *
 * Ne pas confondre avec /a-propos.html, page statique servie par nginx pour
 * les robots d'indexation. Les deux coexistent volontairement.
 */

import { useState } from 'react';
import { useLang } from './i18n';

const SECTIONS = ['about.s1', 'about.s2', 'about.s3', 'about.s4', 'about.s5', 'about.s6', 'about.s7'] as const;

export default function About() {
	const { t } = useLang();
	const [section, setSection] = useState(0);

	return (
		<div className="about">
			<nav className="about-nav">
				{SECTIONS.map((key, index) => (
					<button
						key={key}
						className={index === section ? 'on' : undefined}
						onClick={() => setSection(index)}
					>
						{String(index + 1).padStart(2, '0')} · {t(key)}
					</button>
				))}
			</nav>

			<div className="about-body">
				<h1 className="display-sm">{t('about.title')}</h1>
				<p className="about-lede">{t('about.lede')}</p>
				<hr className="about-rule" />

				{section === 0 && (
					<>
						<h2 className="about-h">
							01 — {t('about.s1')}
						</h2>
						<p>{t('about.p1')}</p>
						<p>{t('about.p2')}</p>
						<div className="about-callout">
							<strong>{t('about.privacy.strong')}</strong> {t('about.privacy')}
						</div>
						<h2 className="about-h">{t('about.req')}</h2>
						<dl className="about-table">
							<div>
								<dt>{t('about.req.browser')}</dt>
								<dd>{t('about.req.browser.v')}</dd>
							</div>
							<div>
								<dt>{t('about.req.tech')}</dt>
								<dd>{t('about.req.tech.v')}</dd>
							</div>
							<div>
								<dt>{t('about.req.conn')}</dt>
								<dd>{t('about.req.conn.v')}</dd>
							</div>
							<div>
								<dt>{t('about.req.game')}</dt>
								<dd>{t('about.req.game.v')}</dd>
							</div>
						</dl>
					</>
				)}

				{section === 1 && (
					<>
						<h2 className="about-h">02 — {t('about.s2')}</h2>
						<p>
							{t('about.fork').split('Play!')[0]}
							<a href="https://github.com/jpd002/Play-" target="_blank" rel="noreferrer">
								Play!
							</a>
							{t('about.fork').split('Play!')[1]}
						</p>
					</>
				)}

				{section === 2 && (
					<>
						<h2 className="about-h">03 — {t('about.s3')}</h2>
						<p>{t('about.why')}</p>
						<ol className="about-steps">
							<li>
								<span className="about-tag">{t('about.fix1.tag')}</span>
								<div>
									<strong>{t('about.fix1.strong')}</strong> {t('about.fix1')}
								</div>
							</li>
							<li>
								<span className="about-tag">{t('about.fix2.tag')}</span>
								<div>
									<strong>{t('about.fix2.strong')}</strong> {t('about.fix2')}
								</div>
							</li>
							<li>
								<span className="about-tag">{t('about.fix3.tag')}</span>
								<div>
									<strong>{t('about.fix3.strong')}</strong> {t('about.fix3')}
								</div>
							</li>
						</ol>
						<p className="about-lede">{t('about.fix.note')}</p>
					</>
				)}

				{section === 3 && (
					<>
						<h2 className="about-h">04 — {t('about.s4')}</h2>
						<p>{t('about.web')}</p>
						<ol className="about-steps">
							<li>
								<span className="about-tag">{t('about.web1.tag')}</span>
								<div>
									<strong>{t('about.web1.strong')}</strong> {t('about.web1')}
								</div>
							</li>
							<li>
								<span className="about-tag">{t('about.web2.tag')}</span>
								<div>
									<strong>{t('about.web2.strong')}</strong> {t('about.web2')}
								</div>
							</li>
							<li>
								<span className="about-tag">{t('about.web3.tag')}</span>
								<div>
									<strong>{t('about.web3.strong')}</strong> {t('about.web3')}
								</div>
							</li>
						</ol>
					</>
				)}

				{section === 4 && (
					<>
						<h2 className="about-h">05 — {t('about.s5')}</h2>
						<p>{t('about.ui')}</p>
					</>
				)}

				{section === 5 && (
					<>
						<h2 className="about-h">06 — {t('about.s6')}</h2>
						<p>
							<strong>{t('about.chrome.strong')}</strong> — {t('about.chrome')}
						</p>
						<p>
							<strong>{t('about.firefox.strong')}</strong> — {t('about.firefox')}
						</p>
					</>
				)}

				{section === 6 && (
					<>
						<h2 className="about-h">07 — {t('about.s7')}</h2>
						<ul className="about-plain">
							<li>{t('about.lim1')}</li>
							<li>{t('about.lim2')}</li>
							<li>{t('about.lim3')}</li>
							<li>{t('about.lim4')}</li>
						</ul>
					</>
				)}
			</div>
		</div>
	);
}
